/**
 * SEARCH CAMPAIGN WORKSPACE — /google-ads/<leads|traffic>/create/campaign
 *
 * Same shape as the YouTube and App Promotion workspaces (step rail · goal
 * strip · sticky live preview), tuned to Google Search:
 *
 *   1. Campaign settings — website, name, bidding (conversion strategies lock
 *      when the account has no conversion tracking), budget, dates, networks,
 *      locations, languages and, for Leads, a Google lead form.
 *   2. Ad groups & ads — MANY ad groups, each with keywords (per-keyword
 *      match type), negatives and MANY responsive search ads.
 *   3. Review & save — grouped facts, every issue, and a live build log.
 *
 * "Start with AI" reads the website and drafts the whole campaign; step 2
 * can suggest keywords or write any single ad. Publishing is RESUMABLE:
 * every created entity is remembered by draft id, so a Google rejection can
 * be fixed and re-published without creating anything twice. The lead form
 * is attached in the same atomic request as the campaign.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import clsx from 'clsx';
import toast from 'react-hot-toast';
import {
  Check, CheckCircle2, ChevronRight, CircleAlert, ClipboardList, ExternalLink, FileText, Globe, Layers, Lock,
  Plus, RefreshCw, Settings, Sparkles, Wand2, XCircle,
} from 'lucide-react';
import { Topbar } from '@/components/Topbar';
import { Button } from '@/components/Button';
import { Card } from '@/components/Card';
import { Input } from '@/components/Input';
import { EmptyState } from '@/components/EmptyState';
import {
  googleAdsService, GoogleAdsGeoSuggestion, GoogleAdsLanguage, GoogleAdsLeadFormSummary, SearchAiSite, SearchBidding,
  SearchConversionReadiness,
} from '@/services/google-ads.service';
import { AiAssistantPanel } from '@/components/ai-assistant/AiAssistantPanel';
import { createSearchPlaybook, SearchAiField, SearchAiForm } from '@/components/ai-assistant/playbooks/searchPlaybook';
import {
  BackTo, ErrorBox, errMsg, Label, pretty, Section, Switch, Tag, useYoutubeAuth,
} from '@/components/google-ads/youtube/youtubeShared';
import { LanguagePicker, LocationSearch } from '@/components/google-ads/youtube/TargetingPickers';
import { hostOf, isHttpUrl, MoneyInput, Pub, StepIcon, toMicros, today } from '@/components/google-ads/objectives/objectiveShared';
import {
  AiBadge, adStrength, BiddingCards, biddingTitle, ChipList, cloneSearchGroup, Fact, groupSummary, KeywordDraft, MATCH_LABEL,
  needsConversions, newSearchGroup, rsaIssues, SearchAdPreview, SearchGroupDraft, searchGroupIssues, SearchObjectiveConfig,
  StrengthPill,
} from './searchShared';
import { GroupDraftCard } from './SearchDraftCards';
import { emptyAdsOf, useSearchAi } from './useSearchAi';

const STEPS = [
  { key: 'campaign', label: 'Campaign settings', icon: Settings },
  { key: 'groups', label: 'Ad groups & ads', icon: Layers },
  { key: 'review', label: 'Review & save', icon: CheckCircle2 },
] as const;
type StepKey = (typeof STEPS)[number]['key'];

const filled = (xs: string[]) => xs.map((x) => x.trim()).filter(Boolean);
const fmtKeyword = (k: KeywordDraft) => (k.matchType === 'EXACT' ? `[${k.text}]` : k.matchType === 'PHRASE' ? `"${k.text}"` : k.text);

/** One network toggle row. */
function NetworkRow({ title, desc, checked, onChange, locked, warn }: {
  title: string; desc: string; checked: boolean; onChange?: (v: boolean) => void; locked?: boolean; warn?: string;
}) {
  return (
    <div className="flex items-start gap-3 px-3.5 py-3">
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 text-sm font-medium text-text-primary">
          {title}{locked && <span className="rounded-full bg-surface-alt px-1.5 py-0.5 text-[10px] font-medium text-text-muted">always on</span>}
        </div>
        <p className="mt-0.5 text-2xs leading-4 text-text-muted">{desc}</p>
        {warn && <p className="mt-1 text-2xs text-warning">{warn}</p>}
      </div>
      <Switch checked={checked} disabled={locked} label={title} onCheckedChange={(v) => onChange?.(v)} />
    </div>
  );
}

/** Lead-form choice card. */
function FormCard({ on, onClick, title, desc, chips, disabled }: {
  on: boolean; onClick: () => void; title: string; desc?: string; chips?: string[]; disabled?: boolean;
}) {
  return (
    <button type="button" onClick={onClick} disabled={disabled} aria-pressed={on}
      className={clsx('flex items-start gap-3 rounded-xl border p-3.5 text-left transition-colors disabled:cursor-not-allowed disabled:opacity-60',
        on ? 'border-primary bg-primary-soft/40' : 'border-border hover:bg-surface-alt')}>
      <span className={clsx('mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full border', on ? 'border-primary' : 'border-border-strong')}>
        {on && <span className="h-2 w-2 rounded-full bg-primary" />}
      </span>
      <span className="min-w-0">
        <span className="block truncate text-sm font-medium text-text-primary">{title}</span>
        {desc && <span className="mt-0.5 block text-2xs leading-4 text-text-muted">{desc}</span>}
        {chips && chips.length > 0 && (
          <span className="mt-1.5 flex flex-wrap gap-1">
            {chips.slice(0, 5).map((c) => <span key={c} className="rounded-full bg-surface-alt px-1.5 py-0.5 text-[10px] text-text-secondary">{pretty(c)}</span>)}
            {chips.length > 5 && <span className="text-[10px] text-text-muted">+{chips.length - 5}</span>}
          </span>
        )}
      </span>
    </button>
  );
}

export default function SearchCampaignWizard({ objective: cfg }: { objective: SearchObjectiveConfig }) {
  const navigate = useNavigate();
  const { auth, accountUid } = useYoutubeAuth();
  const ready = !!(auth?.connected && auth?.customer_selected && accountUid);
  const currency = auth?.currency ?? '';
  const leads = cfg.key === 'leads';

  const [step, setStep] = useState<StepKey>('campaign');

  // ── campaign settings ──
  const [website, setWebsite] = useState('');
  const [name, setName] = useState(`${leads ? 'Leads' : 'Traffic'} - Search - ${today()}`);
  const [bidding, setBiddingState] = useState<SearchBidding>(cfg.defaultBidding);
  const biddingTouched = useRef(false);
  const setBidding = (b: SearchBidding) => { biddingTouched.current = true; setBiddingState(b); };
  const [ceiling, setCeiling] = useState('');
  const [targetCpa, setTargetCpa] = useState('');
  const [targetRoas, setTargetRoas] = useState('');
  const [budget, setBudget] = useState('');
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');
  const [searchPartners, setSearchPartners] = useState(true);
  const [displayNetwork, setDisplayNetwork] = useState(false);
  const [locations, setLocations] = useState<GoogleAdsGeoSuggestion[]>([]);
  const [languages, setLanguages] = useState<GoogleAdsLanguage[]>([]);
  const [leadForms, setLeadForms] = useState<GoogleAdsLeadFormSummary[] | null>(null);
  const [leadFormsError, setLeadFormsError] = useState<string | null>(null);
  const [leadFormId, setLeadFormId] = useState('');

  // ── account facts ──
  const [readiness, setReadiness] = useState<SearchConversionReadiness | null>(null);
  const [takenNames, setTakenNames] = useState<Set<string>>(() => new Set());

  // ── structure ──
  const first = useMemo(() => newSearchGroup(1), []);
  const [groups, setGroups] = useState<SearchGroupDraft[]>([first]);
  const [openGroup, setOpenGroup] = useState<string>(first.id);
  const [activeAd, setActiveAd] = useState<string>(first.ads[0].id);
  const patchGroup = (gid: string, p: Partial<SearchGroupDraft>) => setGroups((gs) => gs.map((g) => (g.id === gid ? { ...g, ...p } : g)));

  // ── AI ──
  const [aiOpen, setAiOpen] = useState(false);
  const [aiFields, setAiFields] = useState<Set<SearchAiField>>(() => new Set());
  const [aiSite, setAiSite] = useState<SearchAiSite | null>(null);
  const [aiSession, setAiSession] = useState<string | undefined>(undefined);
  const isAi = (f: SearchAiField) => aiFields.has(f);
  const clearAi = useCallback((...fields: SearchAiField[]) => {
    setAiFields((prev) => {
      if (!fields.some((f) => prev.has(f))) return prev;
      const next = new Set(prev); fields.forEach((f) => next.delete(f)); return next;
    });
  }, []);
  const playbook = useMemo(() => createSearchPlaybook({ accountUid, currency: currency || undefined, objective: cfg.key }), [accountUid, currency, cfg.key]);

  // ── publish ──
  const [pub, setPub] = useState<Record<string, Pub>>({});
  const [pubError, setPubError] = useState<string | null>(null);
  const [publishing, setPublishing] = useState(false);
  const [finished, setFinished] = useState(false);
  const madeRef = useRef<{ campaign?: string; groups: Record<string, string>; ads: Record<string, string> }>({ groups: {}, ads: {} });
  const campaignLocked = !!madeRef.current.campaign;

  const loadLeadForms = useCallback(async () => {
    if (!accountUid || !cfg.leadForms) return;
    setLeadForms(null); setLeadFormsError(null);
    try { setLeadForms(await googleAdsService.listLeadForms(accountUid)); }
    catch (e) { setLeadForms([]); setLeadFormsError(errMsg(e, 'Could not load lead forms.')); }
  }, [accountUid, cfg.leadForms]);

  useEffect(() => {
    if (!accountUid) return;
    googleAdsService.search.conversionReadiness(accountUid).then(setReadiness).catch(() => setReadiness(null));
    googleAdsService.listCampaigns(accountUid)
      .then((l) => setTakenNames(new Set(l.filter((c) => c.status !== 'REMOVED').map((c) => c.name.trim().toLowerCase()))))
      .catch(() => undefined);
    loadLeadForms();
  }, [accountUid, loadLeadForms]);

  const conversionOk = readiness?.conversionBiddingAllowed === true;
  const conversionBlocked = readiness?.conversionBiddingAllowed === false;
  const recommended: SearchBidding = leads && conversionOk ? 'MAXIMIZE_CONVERSIONS' : 'MAXIMIZE_CLICKS';
  // Leads starts on Maximize clicks and moves to Maximize conversions only once Google confirms tracking exists.
  useEffect(() => {
    if (!readiness || biddingTouched.current) return;
    setBiddingState(recommended);
  }, [readiness, recommended]);
  useEffect(() => {
    if (conversionBlocked && needsConversions(bidding)) setBiddingState('MAXIMIZE_CLICKS');
  }, [conversionBlocked, bidding]);

  const manualCpc = bidding === 'MANUAL_CPC';
  const siteUrl = isHttpUrl(website) ? website.trim() : '';
  const adLanguage = useMemo(() => {
    const c = (languages[0]?.code ?? '').toLowerCase();
    return /^[a-z]{2}/.test(c) ? c.slice(0, 2) : undefined;
  }, [languages]);

  /** The website fills every ad that has no landing page yet. */
  const applyWebsite = () => {
    if (!siteUrl) return;
    setGroups((gs) => gs.map((g) => ({ ...g, ads: g.ads.map((a) => (a.finalUrl ? a : { ...a, finalUrl: siteUrl })) })));
  };

  // ── AI actions (step 2) ──
  const placeNames = useMemo(() => locations.map((l) => l.name), [locations]);
  const ai = useSearchAi({ objective: cfg.key, setGroups, sessionId: aiSession, siteUrl, language: adLanguage, places: placeNames });
  const { setAiAds } = ai;
  const emptyAds = useMemo(() => emptyAdsOf(groups, (id) => !!madeRef.current.ads[id]), [groups, pub]); // eslint-disable-line react-hooks/exhaustive-deps
  const aiForm = useMemo<SearchAiForm>(() => ({
    setWebsite, setName, setBudget, setStart, setEnd, setLocations, setLanguages, setSearchPartners, setDisplayNetwork,
    setBidding: (b) => { biddingTouched.current = true; setBiddingState(b); },
    setGroups: (gs) => {
      setGroups(gs);
      if (gs[0]) { setOpenGroup(gs[0].id); setActiveAd(gs[0].ads[0]?.id ?? ''); }
      setAiAds(new Set(gs.flatMap((g) => g.ads.map((a) => a.id))));
    },
    markAi: (fields) => setAiFields((prev) => new Set([...Array.from(prev), ...fields])),
    setAiContext: (ctx) => { setAiSession(ctx.sessionId); setAiSite(ctx.site); },
  }), [setAiAds]);

  // ── validation ──
  const campaignIssues = useMemo(() => {
    const i: string[] = [];
    if (website.trim() && !siteUrl) i.push('The website must be a full address starting with https://');
    if (!name.trim()) i.push('Campaign name is required.');
    else if (!campaignLocked && takenNames.has(name.trim().toLowerCase())) i.push(`A campaign named "${name.trim()}" already exists on this account — Google needs a new name.`);
    if (!budget || toMicros(budget) <= 0) i.push('Enter a daily budget.');
    if (start && end && end < start) i.push('The end date is before the start date.');
    if (needsConversions(bidding) && conversionBlocked) i.push(`${biddingTitle(bidding)} needs conversion tracking, which this account does not have yet — pick Maximize clicks or Manual CPC.`);
    if (bidding === 'MAXIMIZE_CONVERSION_VALUE' && targetRoas && !(Number(targetRoas) > 0)) i.push('Target ROAS must be above zero.');
    return i;
  }, [website, siteUrl, name, campaignLocked, takenNames, budget, start, end, bidding, conversionBlocked, targetRoas]);

  const groupIssues = useMemo(() => {
    const i: string[] = [];
    if (groups.length === 0) i.push('Add at least one ad group.');
    groups.forEach((g, n) => {
      const label = g.name.trim() || `Ad group ${n + 1}`;
      const gi = searchGroupIssues(g, manualCpc);
      if (gi.length) i.push(`${label}: needs ${gi.join(', ')}.`);
      g.ads.forEach((a, k) => {
        const ai = rsaIssues(a);
        if (ai.length) i.push(`${label} → ${a.name.trim() || `Ad ${k + 1}`}: needs ${ai.join(', ')}.`);
      });
    });
    return i;
  }, [groups, manualCpc]);

  const allIssues = [...campaignIssues, ...groupIssues];
  const issuesFor = (k: StepKey) => (k === 'campaign' ? campaignIssues : k === 'groups' ? groupIssues : []);
  const stepIdx = STEPS.findIndex((s) => s.key === step);

  const allAds = useMemo(() => groups.flatMap((g) => g.ads.map((a) => ({ g, a }))), [groups]);
  const active = allAds.find((x) => x.a.id === activeAd) ?? allAds[0] ?? null;
  const kwCount = groups.reduce((n, g) => n + g.keywords.length, 0);
  const leadForm = leadForms?.find((f) => f.id === leadFormId) ?? null;
  const networksLabel = ['Google Search', searchPartners && 'Search partners', displayNetwork && 'Display Network'].filter(Boolean).join(' · ');
  const biddingSub = bidding === 'MAXIMIZE_CLICKS' && toMicros(ceiling) > 0 ? `Max CPC ${ceiling} ${currency}`
    : bidding === 'MAXIMIZE_CONVERSIONS' && toMicros(targetCpa) > 0 ? `Target CPA ${targetCpa} ${currency}`
    : bidding === 'MAXIMIZE_CONVERSION_VALUE' && Number(targetRoas) > 0 ? `Target ROAS ${Math.round(Number(targetRoas) * 100)}%`
    : bidding === 'MANUAL_CPC' ? 'Max CPC set per ad group' : 'No cap';

  // ── publish (resumable) ──
  const publish = async () => {
    if (!accountUid || allIssues.length) return;
    setPublishing(true); setPubError(null);
    const mark = (k: string, v: Pub) => setPub((m) => ({ ...m, [k]: v }));
    const made = madeRef.current;
    try {
      if (!made.campaign) {
        mark('campaign', 'running');
        try {
          const r = await googleAdsService.search.createCampaign(accountUid, {
            name: name.trim(), dailyBudgetMicros: toMicros(budget), status: 'PAUSED', bidding, objective: cfg.key,
            cpcBidCeilingMicros: bidding === 'MAXIMIZE_CLICKS' && toMicros(ceiling) > 0 ? toMicros(ceiling) : undefined,
            targetCpaMicros: bidding === 'MAXIMIZE_CONVERSIONS' && toMicros(targetCpa) > 0 ? toMicros(targetCpa) : undefined,
            targetRoas: bidding === 'MAXIMIZE_CONVERSION_VALUE' && Number(targetRoas) > 0 ? Number(targetRoas) : undefined,
            searchPartners, displayNetwork,
            startDate: start || undefined, endDate: end || undefined,
            geoTargetIds: locations.length ? locations.map((l) => l.id) : undefined,
            leadFormAssetId: cfg.leadForms && leadFormId ? leadFormId : undefined,
          });
          made.campaign = r.campaignId;
        } catch (e) { mark('campaign', 'failed'); setPubError(`Campaign: ${errMsg(e)}`); return; }
        mark('campaign', 'done');
      }
      for (const g of groups) {
        if (!made.groups[g.id]) {
          mark(g.id, 'running');
          try {
            const r = await googleAdsService.search.createAdGroup(accountUid, {
              campaignId: made.campaign!, name: g.name.trim(), status: 'PAUSED',
              cpcBidMicros: manualCpc ? toMicros(g.cpcBid) : undefined,
              keywords: g.keywords.map((k) => ({ text: k.text, matchType: k.matchType })),
              negativeKeywords: g.negatives.map((k) => ({ text: k.text, matchType: k.matchType })),
            });
            made.groups[g.id] = r.adGroupId;
          } catch (e) { mark(g.id, 'failed'); setPubError(`Ad group "${g.name.trim()}": ${errMsg(e)}`); return; }
          mark(g.id, 'done');
        }
        for (const a of g.ads) {
          if (made.ads[a.id]) continue;
          mark(a.id, 'running');
          try {
            const r = await googleAdsService.search.createAd(accountUid, {
              adGroupId: made.groups[g.id], finalUrls: [a.finalUrl.trim()],
              headlines: filled(a.headlines), descriptions: filled(a.descriptions),
              path1: a.path1 || undefined, path2: a.path2 || undefined, status: 'PAUSED',
            });
            made.ads[a.id] = r.resourceName;
          } catch (e) { mark(a.id, 'failed'); setPubError(`Ad "${a.name.trim() || filled(a.headlines)[0]}" in ${g.name.trim()}: ${errMsg(e)}`); return; }
          mark(a.id, 'done');
        }
      }
      setFinished(true);
      toast.success(`${cfg.title} campaign published — everything is paused, so nothing spends until you enable it.`);
      setTimeout(() => navigate(cfg.base), 1200);
    } finally { setPublishing(false); }
  };

  const addGroup = () => {
    const g = newSearchGroup(groups.length + 1, siteUrl);
    setGroups((gs) => [...gs, g]); setOpenGroup(g.id); setActiveAd(g.ads[0].id);
  };

  return (
    <>
      <Topbar
        title={`New ${cfg.title} campaign`}
        breadcrumb={`Google Ads / ${cfg.title}`}
        description="Search campaign, ad groups, keywords and ads — then save everything paused"
        back={<BackTo to={cfg.base} label={`Back to ${cfg.title}`} />}
      />

      <div className="w-full px-7 py-7">
        {auth === undefined && (
          <div className="flex min-h-[50vh] items-center justify-center">
            <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
          </div>
        )}

        {auth !== undefined && !ready && (
          <EmptyState
            icon={<Globe size={36} className="text-text-muted" />}
            title="Connect a Google Ads account first"
            description="Authorise Google and choose a customer account, then come back to build campaigns."
            action={<Link to="/google-ads/account"><Button iconLeft={<Globe size={14} />}>Connect Google Ads</Button></Link>}
          />
        )}

        {ready && (
          <div className="flex w-full gap-5 pb-12">
            {/* step rail */}
            <div className="hidden w-52 shrink-0 lg:block">
              <Card className="sticky top-24 p-3">
                {STEPS.map((s, i) => {
                  const Icon = s.icon;
                  const on = s.key === step;
                  const done = i < stepIdx && issuesFor(s.key).length === 0;
                  const bad = i < stepIdx && issuesFor(s.key).length > 0;
                  return (
                    <button key={s.key} type="button" onClick={() => setStep(s.key)}
                      className={clsx('mb-1 flex w-full items-center gap-2.5 rounded-xl px-3 py-2.5 text-left text-sm transition-colors',
                        on ? 'bg-primary-soft font-medium text-primary' : 'text-text-secondary hover:bg-surface-alt')}>
                      {done ? <CheckCircle2 size={16} className="shrink-0 text-success" />
                        : bad ? <XCircle size={16} className="shrink-0 text-danger" />
                        : <Icon size={16} className="shrink-0" />}
                      {s.label}
                    </button>
                  );
                })}
                <div className="mt-3 space-y-0.5 border-t border-border px-3 pt-3 text-2xs text-text-muted">
                  <div>{groups.length} ad group{groups.length === 1 ? '' : 's'}</div>
                  <div>{allAds.length} ad{allAds.length === 1 ? '' : 's'}</div>
                  <div>{kwCount} keyword{kwCount === 1 ? '' : 's'}</div>
                  {cfg.leadForms && <div>{leadForm ? 'Lead form attached' : 'No lead form'}</div>}
                </div>
              </Card>
            </div>

            {/* main */}
            <div className="min-w-0 flex-1 space-y-4">
              <div className="flex gap-1 overflow-x-auto rounded-xl border border-border bg-surface p-1 lg:hidden">
                {STEPS.map((s, i) => (
                  <button key={s.key} type="button" onClick={() => setStep(s.key)}
                    className={clsx('shrink-0 rounded-lg px-3 py-1.5 text-xs font-medium', s.key === step ? 'bg-primary-soft text-primary' : 'text-text-secondary')}>
                    {i + 1}. {s.label}
                  </button>
                ))}
              </div>

              <Card padded={false} className="flex flex-wrap items-center gap-x-5 gap-y-1 px-4 py-2.5 text-xs text-text-muted">
                <span>Goal: <span className="font-medium text-text-primary">{cfg.goal}</span></span>
                <span>Type: <span className="font-medium text-text-primary">Search</span></span>
                <span>Served via: <span className="font-medium text-text-primary">{networksLabel}</span></span>
                {cfg.leadForms && leadForm && <Tag tone="ok">lead form</Tag>}
                <Tag>starts paused</Tag>
              </Card>

              {/* ── STEP 1: campaign ── */}
              {step === 'campaign' && (
                <>
                  {campaignLocked && (
                    <div className="flex items-start gap-2.5 rounded-xl border border-info/30 bg-info-soft/40 px-4 py-3 text-xs text-text-secondary">
                      <Lock size={14} className="mt-0.5 shrink-0 text-info" />
                      The campaign already exists on Google, so its settings are locked. Fix the failing ad group or ad and publish again.
                    </div>
                  )}
                  <fieldset disabled={campaignLocked} className="min-w-0 space-y-4">
                    {aiOpen ? (
                      <AiAssistantPanel playbook={playbook} form={aiForm} onClose={() => setAiOpen(false)}
                        onApplied={() => toast.success('Draft applied — check each step, then publish.')} />
                    ) : (
                      <div className="flex flex-wrap items-center gap-4 rounded-2xl border border-primary/30 bg-primary-soft/25 px-4 py-3.5">
                        <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-primary text-white"><Sparkles size={18} /></span>
                        <span className="min-w-[220px] flex-1">
                          <span className="block text-[15px] font-semibold text-text-primary">AI assistant for {cfg.title} campaigns</span>
                          <span className="block text-xs text-text-muted">Paste your website, answer four questions, and every field — ad groups, keywords and ads included — gets filled in. You review everything; nothing publishes on its own.</span>
                        </span>
                        <Button onClick={() => setAiOpen(true)} iconLeft={<Sparkles size={14} />} className="shrink-0">Start with AI</Button>
                      </div>
                    )}

                    <Section title="Your website" hint="Ads link here unless you give an ad its own page. The AI reads it to write keywords and ads." right={<AiBadge on={isAi('website')} />}>
                      <Input iconLeft={<Globe size={14} />} placeholder="https://yourbusiness.com" value={website}
                        onChange={(e) => { setWebsite(e.target.value); clearAi('website'); }} onBlur={applyWebsite} />
                      {siteUrl && <p className="mt-1.5 flex items-center gap-1 text-2xs text-success"><Check size={11} /> {hostOf(siteUrl)} — new ads start with this landing page.</p>}
                    </Section>

                    <Section title="Campaign name" right={<AiBadge on={isAi('name')} />}>
                      <Input value={name} maxLength={255} onChange={(e) => { setName(e.target.value); clearAi('name'); }} />
                    </Section>

                    <Section title="Bidding" hint="How Google decides what each click is worth." right={<AiBadge on={isAi('bidding')} />}>
                      {readiness && (
                        <div className={clsx('mb-3 flex items-start gap-2 rounded-lg px-3 py-2 text-2xs',
                          conversionOk ? 'bg-success-soft text-success' : conversionBlocked ? 'bg-warning-soft text-warning' : 'bg-surface-alt text-text-muted')}>
                          {conversionOk ? <CheckCircle2 size={13} className="mt-px shrink-0" /> : <CircleAlert size={13} className="mt-px shrink-0" />}
                          <span>
                            {conversionOk
                              ? `Conversion tracking is set up on this account${readiness.enabledConversionActions ? ` (${readiness.enabledConversionActions} conversion action${readiness.enabledConversionActions === 1 ? '' : 's'})` : ''} — conversion strategies are available.`
                              : conversionBlocked
                                ? 'This account has no conversion tracking yet, so Google refuses conversion strategies. Set it up in Google Ads → Goals → Conversions to unlock them.'
                                : 'Could not check conversion tracking — conversion strategies only work once it is set up.'}
                          </span>
                        </div>
                      )}
                      <BiddingCards value={bidding} onChange={(b) => { setBidding(b); clearAi('bidding'); }} recommended={recommended} conversionBlocked={conversionBlocked} />
                      {(bidding !== 'MANUAL_CPC') && (
                        <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
                          {bidding === 'MAXIMIZE_CLICKS' && (
                            <div>
                              <Label>Max CPC cap <span className="font-normal text-text-muted">(optional)</span></Label>
                              <div className="mt-1.5"><MoneyInput value={ceiling} onChange={setCeiling} currency={currency} placeholder="No cap" /></div>
                              <p className="mt-1 text-2xs text-text-muted">The most Google may pay for one click.</p>
                            </div>
                          )}
                          {bidding === 'MAXIMIZE_CONVERSIONS' && (
                            <div>
                              <Label>Target cost per lead <span className="font-normal text-text-muted">(optional)</span></Label>
                              <div className="mt-1.5"><MoneyInput value={targetCpa} onChange={setTargetCpa} currency={currency} placeholder="Let Google decide" /></div>
                              <p className="mt-1 text-2xs text-text-muted">Leave blank for the first weeks — Google learns faster without a target.</p>
                            </div>
                          )}
                          {bidding === 'MAXIMIZE_CONVERSION_VALUE' && (
                            <div>
                              <Label>Target ROAS <span className="font-normal text-text-muted">(optional — 3 = 300%)</span></Label>
                              <Input className="mt-1.5" inputMode="decimal" value={targetRoas} onChange={(e) => setTargetRoas(e.target.value)} placeholder="e.g. 3" />
                            </div>
                          )}
                        </div>
                      )}
                      {manualCpc && <p className="mt-3 text-2xs text-text-muted">You set a max CPC on each ad group in the next step.</p>}
                    </Section>

                    <Section title="Budget and dates" hint="Google may spend up to 2× the daily budget on a busy day and evens it out over the month." right={<AiBadge on={isAi('budget') || isAi('dates')} />}>
                      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                        <div>
                          <Label>Daily budget{currency ? ` (${currency})` : ''}</Label>
                          <div className="mt-1.5"><MoneyInput value={budget} onChange={(v) => { setBudget(v); clearAi('budget'); }} currency={currency} placeholder="e.g. 50" /></div>
                        </div>
                        <div>
                          <Label>Monthly estimate</Label>
                          <div className="mt-1.5 flex h-10 items-center rounded-lg bg-surface-alt px-3.5 text-sm text-text-secondary">
                            {toMicros(budget) > 0 ? `≈ ${(Number(budget) * 30.4).toLocaleString(undefined, { maximumFractionDigits: 0 })} ${currency}` : '—'}
                          </div>
                        </div>
                        <div>
                          <Label>Start date</Label>
                          <Input className="mt-1.5" type="date" value={start} min={today()} onChange={(e) => { setStart(e.target.value); clearAi('dates'); }} />
                          <p className="mt-1 text-2xs text-text-muted">Blank = starts when you enable it.</p>
                        </div>
                        <div>
                          <Label>End date <span className="font-normal text-text-muted">(optional)</span></Label>
                          <Input className="mt-1.5" type="date" value={end} min={start || today()} onChange={(e) => { setEnd(e.target.value); clearAi('dates'); }} />
                        </div>
                      </div>
                    </Section>

                    <Section title="Networks" hint="Where the ads may show." right={<AiBadge on={isAi('networks')} />}>
                      <div className="divide-y divide-border rounded-xl border border-border">
                        <NetworkRow title="Google Search" desc="Your ads on Google's search results." checked locked />
                        <NetworkRow title="Search partners" desc="Partner search sites and other Google properties — usually cheap extra reach."
                          checked={searchPartners} onChange={(v) => { setSearchPartners(v); clearAi('networks'); }} />
                        <NetworkRow title="Display Network expansion" desc="Lets the campaign show on websites when the budget allows. Most Search advertisers keep it off."
                          checked={displayNetwork} onChange={(v) => { setDisplayNetwork(v); clearAi('networks'); }}
                          warn={displayNetwork ? 'Display clicks are cheaper but rarely turn into leads like searches do — watch the spend.' : undefined} />
                      </div>
                    </Section>

                    <Section title="Locations" hint="Searched live from Google — countries, regions and cities. Empty = everywhere Google serves." right={<AiBadge on={isAi('locations')} />}>
                      <LocationSearch accountUid={accountUid} value={locations} onChange={(v) => { setLocations(v); clearAi('locations'); }} />
                    </Section>

                    <Section title="Ad language" hint="Google now matches Search ads to the language they are written in, so this picks the language the AI writes your ads in — it no longer limits who sees them." right={<AiBadge on={isAi('languages')} />}>
                      <LanguagePicker accountUid={accountUid} value={languages} onChange={(v) => { setLanguages(v); clearAi('languages'); }} />
                    </Section>

                    {cfg.leadForms && (
                      <Section title="Lead form" hint="Optional. A Google lead form lets people send their details right from the ad — every submission lands in your CRM leads."
                        right={<Button variant="ghost" size="sm" className="h-7 px-2 text-2xs" iconLeft={<RefreshCw size={12} />} onClick={loadLeadForms}>Refresh</Button>}>
                        {leadForms === null ? (
                          <div className="space-y-2">{[0, 1].map((i) => <div key={i} className="skeleton h-14 rounded-xl" />)}</div>
                        ) : (
                          <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2">
                            <FormCard on={!leadFormId} onClick={() => setLeadFormId('')} title="No lead form"
                              desc="People call, WhatsApp or use the form on your website." />
                            {leadForms.map((f) => (
                              <FormCard key={f.id} on={leadFormId === f.id} onClick={() => setLeadFormId(f.id)}
                                title={f.name || f.headline || `Form ${f.id}`} desc={[f.headline, f.callToActionType && pretty(f.callToActionType)].filter(Boolean).join(' · ')}
                                chips={f.fields} />
                            ))}
                          </div>
                        )}
                        {leadFormsError && <p className="mt-2 text-2xs text-warning">{leadFormsError}</p>}
                        {leadForm?.delivery && leadForm.delivery !== 'connected' && leadForm.delivery !== 'unknown' && (
                          <p className="mt-2 text-2xs text-warning">
                            This form does not deliver to Slightor ({leadForm.delivery.replace(/_/g, ' ')}), so its submissions will not become CRM leads until its webhook is fixed in Measurement → Lead forms.
                          </p>
                        )}
                        <p className="mt-2.5 flex flex-wrap items-center gap-1 text-2xs text-text-muted">
                          <ClipboardList size={12} /> Need a form?
                          <a className="inline-flex items-center gap-0.5 text-primary hover:underline" href="/google-ads/tools?tab=lead-forms" target="_blank" rel="noreferrer">
                            Create one in Measurement → Lead forms <ExternalLink size={10} />
                          </a>
                          then press Refresh.
                        </p>
                      </Section>
                    )}
                  </fieldset>

                  <div className="flex justify-end">
                    <Button onClick={() => { applyWebsite(); setStep('groups'); }} iconRight={<ChevronRight size={15} />}>Next: Ad groups & ads</Button>
                  </div>
                </>
              )}

              {/* ── STEP 2: ad groups + ads ── */}
              {step === 'groups' && (
                <>
                  <div className="flex flex-wrap items-center gap-3 rounded-2xl border border-primary/30 bg-primary-soft/40 px-4 py-3">
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-primary/15 text-primary"><Sparkles size={16} /></span>
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-semibold text-text-primary">
                        {isAi('groups') ? 'AI drafted these ad groups — review every keyword and line' : 'Write keywords and ads with AI'}
                      </div>
                      <div className="text-2xs text-text-muted">
                        {aiSite ? `Grounded in ${aiSite.siteName ?? aiSite.host}` : "Uses each ad's landing page and the group's keywords"}
                        {' · '}"Suggest with AI" adds keyword ideas, "Write with AI" drafts an ad's headlines and descriptions.
                      </div>
                    </div>
                    <Button size="sm" variant={emptyAds.length ? 'primary' : 'secondary'} iconLeft={<Wand2 size={13} />} loading={ai.busy === 'all'}
                      disabled={!!ai.busy || emptyAds.length === 0} onClick={() => ai.writeAll(emptyAds)}>
                      {emptyAds.length ? `Write ${emptyAds.length} empty ad${emptyAds.length === 1 ? '' : 's'}` : 'All ads have copy'}
                    </Button>
                  </div>

                  {groups.map((g, n) => (
                    <GroupDraftCard key={g.id} group={g} index={n} open={openGroup === g.id} onToggle={() => setOpenGroup(openGroup === g.id ? '' : g.id)}
                      manualCpc={manualCpc} currency={currency} pub={pub} madeGroup={!!madeRef.current.groups[g.id]} madeAd={(id) => !!madeRef.current.ads[id]}
                      busy={publishing} onChange={(p) => patchGroup(g.id, p)} defaultUrl={siteUrl}
                      onDuplicate={() => {
                        const c = cloneSearchGroup(g);
                        setGroups((gs) => { const i = gs.findIndex((x) => x.id === g.id); return [...gs.slice(0, i + 1), c, ...gs.slice(i + 1)]; });
                        setOpenGroup(c.id); if (c.ads[0]) setActiveAd(c.ads[0].id);
                      }}
                      onRemove={groups.length > 1 ? () => setGroups((gs) => gs.filter((x) => x.id !== g.id)) : undefined}
                      activeAdId={active?.a.id} onFocusAd={setActiveAd}
                      onSuggest={() => ai.suggest(g)} suggesting={ai.busy === `kw:${g.id}`}
                      onWriteAd={(a) => ai.write(g, a)}
                      writingAd={(id) => ai.busy === `ad:${id}` || (ai.busy === 'all' && emptyAds.some((x) => x.a.id === id))}
                      aiAds={ai.aiAds} onAdCopyEdited={ai.copyEdited} />
                  ))}

                  <Button variant="secondary" className="w-full" iconLeft={<Plus size={15} />} disabled={publishing} onClick={addGroup}>
                    Add another ad group
                  </Button>

                  <div className="flex justify-between">
                    <Button variant="secondary" onClick={() => setStep('campaign')}>Back</Button>
                    <Button onClick={() => setStep('review')} iconRight={<ChevronRight size={15} />}>Next: Review</Button>
                  </div>
                </>
              )}

              {/* ── STEP 3: review ── */}
              {step === 'review' && (
                <>
                  {/* Side by side only on very wide screens — beside the rail and preview each half is too narrow. */}
                  <div className="grid grid-cols-1 gap-4 2xl:grid-cols-[1.1fr_1fr]">
                    <Section title="Campaign" hint="Everything Google will receive for the campaign itself."
                      right={<Button variant="ghost" size="sm" className="h-7 px-2 text-2xs" onClick={() => setStep('campaign')}>Edit</Button>}>
                      <div className="grid grid-cols-1 gap-x-6 gap-y-3 sm:grid-cols-2">
                        <Fact label="Name" value={name || '—'} wide />
                        <Fact label="Goal" value={cfg.title} sub={cfg.goal} />
                        <Fact label="Bidding" value={biddingTitle(bidding)} sub={biddingSub} />
                        <Fact label="Daily budget" value={budget ? `${budget} ${currency}` : '—'}
                          sub={toMicros(budget) > 0 ? `≈ ${(Number(budget) * 30.4).toFixed(0)} ${currency} / month` : undefined} />
                        <Fact label="Schedule" value={start ? `${start} → ${end || 'no end'}` : `Starts when enabled${end ? ` → ends ${end}` : ' · no end date'}`} />
                        <Fact label="Networks" value={networksLabel} wide />
                        {cfg.leadForms && <Fact label="Lead form" value={leadForm ? (leadForm.name || leadForm.headline || leadForm.id) : 'None — people contact you from the landing page'} wide />}
                        {siteUrl && <Fact label="Website" value={siteUrl} wide />}
                      </div>
                      <div className="mt-4 space-y-3 border-t border-border pt-3">
                        <div>
                          <div className="mb-1.5 flex items-baseline justify-between">
                            <span className="text-2xs font-semibold uppercase tracking-wide text-text-muted">Locations</span>
                            <span className="text-2xs text-text-muted">{locations.length ? `${locations.length} selected` : 'Everywhere'}</span>
                          </div>
                          <ChipList items={locations.map((l) => l.name)} empty="Every location Google serves" />
                        </div>
                        <div>
                          <div className="mb-1.5 text-2xs font-semibold uppercase tracking-wide text-text-muted">Ad language</div>
                          <ChipList items={languages.map((l) => l.name)} empty="Matched from the ad text" />
                        </div>
                      </div>
                    </Section>

                    <Section title="Ad groups & ads" hint={`${groups.length} ad group${groups.length === 1 ? '' : 's'} · ${allAds.length} ad${allAds.length === 1 ? '' : 's'} · ${kwCount} keyword${kwCount === 1 ? '' : 's'}`}
                      right={<Button variant="ghost" size="sm" className="h-7 px-2 text-2xs" onClick={() => setStep('groups')}>Edit</Button>}>
                      <div className="space-y-2.5">
                        {groups.map((g, n) => (
                          <div key={g.id} className="rounded-lg border border-border p-2.5">
                            <div className="flex items-center gap-2">
                              <Layers size={13} className="shrink-0 text-text-muted" />
                              <span className="min-w-0 flex-1 truncate text-[13px] font-medium text-text-primary">{g.name.trim() || `Ad group ${n + 1}`}</span>
                              <span className="shrink-0 text-2xs text-text-muted">{groupSummary(g)}{manualCpc ? ` · max CPC ${g.cpcBid}` : ''}</span>
                            </div>
                            <div className="mt-2 pl-5">
                              <ChipList items={g.keywords.map(fmtKeyword)} empty="No keywords yet" max={8} />
                            </div>
                            <div className="mt-2 space-y-0.5 pl-5">
                              {g.ads.map((a, k) => (
                                <button key={a.id} type="button" onClick={() => setActiveAd(a.id)}
                                  className="flex w-full items-center gap-2 rounded px-1 py-1 text-left hover:bg-surface-alt">
                                  <FileText size={11} className="shrink-0 text-text-muted" />
                                  <span className="min-w-0 flex-1 truncate text-[12px] text-text-primary">{filled(a.headlines).slice(0, 2).join(' | ') || a.name || `Ad ${k + 1}`}</span>
                                  <StrengthPill s={adStrength(a, g.keywords)} />
                                </button>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                      <p className="mt-3 text-2xs text-text-muted">
                        Keyword match: {(['BROAD', 'PHRASE', 'EXACT'] as const).map((m) => `${MATCH_LABEL[m]} ${groups.reduce((c, g) => c + g.keywords.filter((k) => k.matchType === m).length, 0)}`).join(' · ')}
                      </p>
                    </Section>
                  </div>

                  {allIssues.length > 0 && (
                    <Section title={`${allIssues.length} thing${allIssues.length === 1 ? '' : 's'} to fix before publishing`}>
                      <ul className="space-y-1.5">
                        {campaignIssues.map((i) => (
                          <li key={i}><button type="button" onClick={() => setStep('campaign')} className="flex items-start gap-2 text-left text-[13px] text-danger hover:underline"><XCircle size={13} className="mt-0.5 shrink-0" /> {i}</button></li>
                        ))}
                        {groupIssues.map((i) => (
                          <li key={i}><button type="button" onClick={() => setStep('groups')} className="flex items-start gap-2 text-left text-[13px] text-danger hover:underline"><XCircle size={13} className="mt-0.5 shrink-0" /> {i}</button></li>
                        ))}
                      </ul>
                    </Section>
                  )}

                  <Section title="Publish" hint="Creates everything on Google in order. The campaign, ad groups and ads all start paused — nothing spends until you enable the campaign.">
                    <div className="space-y-1">
                      <div className="flex items-center gap-2.5 text-sm text-text-primary">
                        <StepIcon s={pub.campaign} /> <span className="truncate">Campaign — {name || 'untitled'}{cfg.leadForms && leadForm ? ' + lead form' : ''}</span>
                      </div>
                      {groups.map((g, n) => (
                        <div key={g.id} className="space-y-1">
                          <div className="flex items-center gap-2.5 pl-5 text-sm text-text-primary">
                            <StepIcon s={pub[g.id]} /> <span className="truncate">Ad group — {g.name.trim() || `Ad group ${n + 1}`} · {g.keywords.length} keyword{g.keywords.length === 1 ? '' : 's'}</span>
                          </div>
                          {g.ads.map((a, k) => (
                            <div key={a.id} className="flex items-center gap-2.5 pl-10 text-[13px] text-text-primary">
                              <StepIcon s={pub[a.id]} /> <span className="truncate">Ad — {filled(a.headlines)[0] || a.name || `Ad ${k + 1}`}</span>
                            </div>
                          ))}
                        </div>
                      ))}
                    </div>

                    {pubError && (
                      <div className="mt-4">
                        <ErrorBox message={pubError} />
                        <p className="mt-1 text-2xs text-text-muted">Fix it and publish again — anything already created is kept, not repeated.</p>
                      </div>
                    )}

                    <div className="mt-5 flex justify-end gap-2">
                      <Button variant="secondary" onClick={() => navigate(cfg.base)} disabled={publishing}>Cancel</Button>
                      <Button onClick={publish} loading={publishing} disabled={publishing || finished || allIssues.length > 0} iconLeft={<Check size={15} />}>
                        {pubError ? 'Retry publish' : 'Save paused campaign'}
                      </Button>
                    </div>
                  </Section>
                </>
              )}
            </div>

            {/* live preview */}
            <div className="hidden w-[320px] shrink-0 xl:block">
              <div className="sticky top-24">
                <SearchAdPreview ad={active?.a ?? null} keywords={active?.g.keywords ?? []} businessName={aiSite?.siteName} />
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
